import React, {FC} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useReconnectHandler} from '../../../../../srcCommon/push/pushContext';
import {OfflineConversationCachingContainer} from '../../../../../srcCommon/services/offline/offlineConversationCaching';
import {ReselectDirectionsEnum} from '../../../../models/api/preferencesModel';
import {FetchCellsCachePoliciesEnum} from '../../../../store/panels/cells/cellsPanelActions';
import {CellsPanelPagesState, CellsPanelPageTypesEnum} from '../../../../store/panels/cells/cellsPanelState';
import {InboxesParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {CellsPanelId} from '../../../../store/panels/panelStateBase';
import {CellsConversationsContainer} from './cellsConversationsContainer';
import {CellsItemsChildrenRenderer} from './cellsItemsBase';
import {CellsMessagesContainer} from './cellsMessagesContainer';

/*
 * Props.
 */

export type CellsContentParametersPathBuilder = (parameters: InboxesParameters) => string;
export type CellsContentChildrenRenderer = CellsItemsChildrenRenderer;

export interface CellsContentProps {
  panelId: CellsPanelId;
  pages: CellsPanelPagesState;
  cachePolicy: FetchCellsCachePoliciesEnum;
  reselectDirection: ReselectDirectionsEnum;
  buildParametersPath?: CellsContentParametersPathBuilder;
  onParametersChange: HandlerOf<InboxesParameters>;
  onReconnect: Handler;
  children: CellsContentChildrenRenderer;
}

/*
 * Component.
 */

export const CellsContent: FC<CellsContentProps> = (props) => {
  const {pages, onReconnect} = props;

  useReconnectHandler(onReconnect);

  return (
    <>
      <OfflineConversationCachingContainer page={pages.current} />
      {renderContent(props)}
    </>
  );
};

function renderContent(props: CellsContentProps) {
  const {panelId, pages, cachePolicy, reselectDirection, buildParametersPath, onParametersChange, children} =
    props;
  const currentPage = pages.current;
  const hasNextPage = Boolean(pages.next);

  switch (currentPage.type) {
    case CellsPanelPageTypesEnum.MESSAGES:
      return (
        <CellsMessagesContainer
          panelId={panelId}
          hasNextPage={hasNextPage}
          page={currentPage}
          cachePolicy={cachePolicy}
          reselectDirection={reselectDirection}
          buildParametersPath={buildParametersPath}
          onParametersChange={onParametersChange}
        >
          {children}
        </CellsMessagesContainer>
      );

    default:
      return (
        <CellsConversationsContainer
          panelId={panelId}
          hasNextPage={hasNextPage}
          page={currentPage}
          cachePolicy={cachePolicy}
          reselectDirection={reselectDirection}
          buildParametersPath={buildParametersPath}
          onParametersChange={onParametersChange}
        >
          {children}
        </CellsConversationsContainer>
      );
  }
}
